import {
  HeartPulse, Brain, Dumbbell, MoonStar, Flower2, HeartHandshake, ShieldCheck,
  PersonStanding, Stethoscope, BookOpen, FlaskConical, Pill, MapPin, Siren, FolderHeart,
  Sunrise, CalendarDays, Layers, Timer, Palette, Users, LifeBuoy,
  ClipboardList, Play, Route, UtensilsCrossed, ChefHat, CalendarRange, Leaf,
  Hourglass, Clock, Music, AlarmClock, RotateCcw,
  CircleDot, Sprout, Baby, Sun, Thermometer, User, Scale, HeartCrack,
  MessageCircle, Network, MessageSquareText, Coffee, CalendarCheck,
  CalendarClock, Search, Syringe, ListTree, Radar, Percent, TrendingUp,
} from "lucide-react";

export const PLATFORMS = [
  {
    key: "health",
    num: "01",
    path: "/health",
    name: "Clinic Desk",
    short: "When something feels wrong",
    icon: HeartPulse,
    hue: "#E26D5A",
    deep: "#B04430",
    soft: "#FBEAE6",
    dark: false,
    navDark: true,
    tagline: "Answers before the waiting room",
    intro: "For the moment something feels wrong and you do not know what to do about it. This place prepares you to talk to a doctor, it never replaces one.",
    core: "Its core is a navigator. You describe what you feel, it hands you the exact questions to ask a doctor.",
    signature: "stagger",
    features: [
      { icon: PersonStanding, path: "/body", name: "The body, explorable", blurb: "Front and back, tappable by region and by system. Heart, lungs, gut, skin, bones, nerves, hormones, blood and more. Each opens to its conditions and what makes something urgent." },
      { icon: Stethoscope, path: "/symptoms", name: "Symptom navigator", blurb: "Describe what you feel, answer a short series of questions. Never a diagnosis. What this could be, what to watch for, and the exact questions to bring to a doctor." },
      { icon: BookOpen, path: "/conditions", name: "Condition library", blurb: "Sixty to eighty conditions in plain terms. What it feels like from the inside, what makes it worse, what a doctor will actually do." },
      { icon: FlaskConical, path: "/results", name: "Lab result decoder", blurb: "Enter your values, get a plain explanation of what each one measures and what a result outside the range usually means. Never alarmist." },
      { icon: Pill, path: "/medications", name: "Medication reference", blurb: "What a drug does, common side effects, missed doses, and an interaction checker across everything you take." },
      { icon: MapPin, path: "/clinics", name: "Clinic map", blurb: "Searchable by specialty, public or private, hours, language and accessibility. Saved favourites." },
      { icon: Siren, path: "/emergency", name: "Red flags, always visible", blurb: "Urgent signs by system, reachable from every page, never behind sign in." },
      { icon: FolderHeart, path: "/file", name: "Your private file", blurb: "Conditions, medications, allergies, results, family history. Generates a one page summary to bring to an appointment." },
    ],
    room: "Questions asked anonymously, answered by verified doctors under their real names.",
  },
  {
    key: "mental",
    num: "02",
    path: "/mental",
    name: "Mental Health",
    short: "A daily companion",
    icon: Brain,
    hue: "#8B72EC",
    deep: "#5D48C0",
    soft: "#EFEBFD",
    dark: false,
    tagline: "One day at a time",
    intro: "The most personal place here. Not a reference to read, a companion that walks with you, one day at a time.",
    core: "Its core is a six week programme. A short read, a specialist audio piece, one action, one creative challenge, one honest reflection each day.",
    signature: "warm",
    features: [
      { icon: Sunrise, path: "/start", name: "The arrival", blurb: "Five questions, first person, under ninety seconds. It shapes everything that follows and ends with a genuine moment of arrival." },
      { icon: CalendarDays, path: "/program", name: "Six weeks, daily", blurb: "One day at a time. Every action carries honest response options, including could not face it today. The button says continue, never begin." },
      { icon: Layers, path: "/topics", name: "The topics", blurb: "Anxiety, low mood, stress, burnout, trauma, anger, grief, loneliness, and dependencies of every weight: alcohol, nicotine, betting, games, the endless feed." },
      { icon: Timer, path: "/tools", name: "The instruments", blurb: "A loop journal mapping trigger, routine and reward. An urge timer that rides out a craving minute by minute. A breathing instrument. A letter to yourself, delivered weeks later." },
      { icon: Palette, path: "/studio", name: "The studio", blurb: "Read, draw, write, listen. A full drawing surface, a clean writing tool, recorded talks and long form listening. Everything you make stays yours." },
      { icon: Users, path: "/circle", name: "The circle", blurb: "Anonymous rooms by topic. Members under aliases, verified specialists replying under full names. Conversation starters for anyone hesitating." },
      { icon: LifeBuoy, path: "/crisis", name: "Crisis route", blurb: "Reachable from every screen. Never behind sign in." },
    ],
    room: "Rooms by topic, one inbox, specialist replies marked distinctly.",
  },
  {
    key: "vitality",
    num: "03",
    path: "/vitality",
    name: "Fitness and Nutrition",
    short: "Your circumstances in, a real plan out",
    icon: Dumbbell,
    hue: "#4CAF7D",
    deep: "#2B7D54",
    soft: "#E6F5EC",
    dark: false,
    tagline: "Built from your real week",
    intro: "The most configurable place here. Your days, your equipment, your injuries and your food go in. A plan you can genuinely follow comes out.",
    core: "Its core is a builder. Not a static PDF. A weekly block you can edit, swap, move and progress.",
    signature: "columns",
    features: [
      { icon: ClipboardList, path: "/train/plan", name: "The plan builder", blurb: "A generated weekly block you can genuinely edit. Swap an exercise, move a day, apply progressive overload, schedule a deload." },
      { icon: Dumbbell, path: "/train/movements", name: "Movement library", blurb: "A hundred and fifty exercises with video, form cues, common mistakes, easier and harder variants, equipment substitutions." },
      { icon: Play, path: "/train/session", name: "Live session player", blurb: "Run the workout, log sets and weight, rest timers, auto advance, finish summary." },
      { icon: Route, path: "/train/tracks", name: "The tracks", blurb: "First month in a gym. Home with nothing. Back after injury. Running from zero. Training past forty. Training in pregnancy." },
      { icon: UtensilsCrossed, path: "/diet/plate", name: "The plate builder", blurb: "Meals composed visually from real components. No calorie counting as the headline, no weight as a primary number." },
      { icon: ChefHat, path: "/diet/recipes", name: "Recipes that fit", blurb: "Filterable by time, cost and what is already in your kitchen. Local and Balkan food done well rather than replaced." },
      { icon: CalendarRange, path: "/diet/week", name: "Your eating week", blurb: "A weekly pattern generated from your schedule, budget and what you actually like, with a shopping list." },
      { icon: Leaf, path: "/diet/supplements", name: "Supplements, graded honestly", blurb: "What the evidence actually says, with interaction warnings." },
    ],
    room: "One room for both halves: training and food.",
  },
  {
    key: "sleep",
    num: "04",
    path: "/sleep",
    name: "Sleep and Energy",
    short: "Shaped like a night",
    icon: MoonStar,
    hue: "#6B74DC",
    deep: "#26235E",
    soft: "#EAECFB",
    dark: true,
    tagline: "Nights measured, not guessed",
    intro: "The one place here that runs dark. Built backwards from the hour you must wake, measured instead of guessed.",
    core: "Its core is an instrument. A wind down runway, a body clock map, and fourteen nights that show you your own shape.",
    signature: "night",
    features: [
      { icon: Hourglass, path: "/runway", name: "The wind down runway", blurb: "Ninety minutes built backwards from your wake time. Customisable steps, timers, light guidance, and a caffeine cut off calculated for you." },
      { icon: Clock, path: "/clock", name: "Body clock map", blurb: "Chronotype, light exposure timing, meal timing, and the shape of energy across your day." },
      { icon: MoonStar, path: "/reset", name: "Fourteen night reset", blurb: "One card each night, one short log each morning. Results shown as a changing night shape, not a chart." },
      { icon: Music, path: "/sounds", name: "The soundscape mixer", blurb: "Rain, embers, wind and low tones, layered with a real mixer and saved as personal mixes." },
      { icon: AlarmClock, path: "/nap", name: "The nap timer", blurb: "It knows the difference between twenty minutes and ninety." },
      { icon: RotateCcw, path: "/shift", name: "Shift work planner", blurb: "For night shifts and rotating schedules, planned rather than survived." },
    ],
    room: "A room for the hours nobody else is awake.",
  },
  {
    key: "reproductive",
    num: "05",
    path: "/reproductive",
    name: "Reproductive Health",
    short: "An atlas that rebuilds around you",
    icon: Flower2,
    hue: "#DE6E96",
    deep: "#B04068",
    soft: "#FBE9F0",
    dark: false,
    tagline: "Your stage, your atlas",
    intro: "You choose where you are in life and the entire place rebuilds around you. Nobody sees content meant for a different stage.",
    core: "Its core is the atlas. Eight stages, each a complete world of its own.",
    signature: "stages",
    features: [
      { icon: CircleDot, path: "/cycles", name: "Cycles", blurb: "Tracking and cycle literacy. PMS and PMDD, painful periods, heavy bleeding, endometriosis, PCOS, missing periods." },
      { icon: Sprout, path: "/trying", name: "Trying", blurb: "Fertility windows, how long is normal, when to seek assessment, male fertility, assisted conception explained without jargon." },
      { icon: Baby, path: "/pregnancy", name: "Pregnancy", blurb: "Week by week. What is happening, appointments and screenings, what is normal and what is a warning sign." },
      { icon: Sun, path: "/after", name: "The first forty days", blurb: "Recovery, pelvic floor, feeding both ways, mood after birth, the identity shift nobody prepares people for." },
      { icon: Thermometer, path: "/midlife", name: "Midlife", blurb: "Perimenopause and menopause. A symptom tracker, hormone therapy explained honestly, what a doctor should be checking." },
      { icon: User, path: "/men", name: "Men", blurb: "Hormonal change across life, prostate, fertility, function concerns. Written directly and without embarrassment." },
      { icon: Scale, path: "/contraception", name: "Contraception compared", blurb: "Every method side by side: effectiveness, side effects, reversibility, cost, what suits which life." },
      { icon: HeartCrack, path: "/loss", name: "Loss", blurb: "Miscarriage, stillbirth, and the material almost nobody publishes well." },
    ],
    room: "A room per stage, so nobody explains themselves twice.",
  },
  {
    key: "relationships",
    num: "06",
    path: "/relationships",
    name: "Relationships and Family",
    short: "It starts with a sentence you recognise",
    icon: HeartHandshake,
    hue: "#E89A4F",
    deep: "#B56A22",
    soft: "#FBF0E2",
    dark: false,
    tagline: "Situations, not categories",
    intro: "Nobody searches for relationship advice. So this place leads with situations, plain sentences you will recognise on sight.",
    core: "Its core is the situations room. Each sentence opens a full path.",
    signature: "statements",
    features: [
      { icon: MessageCircle, path: "/situations", name: "The situations", blurb: "We stopped speaking. I moved and I know nobody. My child will not talk to me. I am the one holding this family together. Each opens a full path." },
      { icon: Network, path: "/map", name: "Your people, mapped", blurb: "Who is close, who has drifted, when you last spoke. Private, never shared, never scored." },
      { icon: MessageSquareText, path: "/scripts", name: "The scripts", blurb: "Conversations that are hard to begin. Apologies that land. Setting a limit. Saying no. Checking in on someone you are worried about." },
      { icon: Users, path: "/parenting", name: "Parenting by age", blurb: "Talking about hard things, limits and screens, teenagers, and being a parent while struggling yourself." },
      { icon: Coffee, path: "/alone", name: "Alone", blurb: "Loneliness taken seriously as the health risk it is. Building a circle from nothing. Adult friendship. Being new in a place." },
      { icon: CalendarCheck, path: "/weekly", name: "One outward action", blurb: "One each week, chosen by you, logged privately." },
    ],
    room: "A room per situation, so you land among people mid sentence.",
  },
  {
    key: "prevention",
    num: "07",
    path: "/prevention",
    name: "Prevention and Screening",
    short: "For people who intend to stay ahead",
    icon: ShieldCheck,
    hue: "#4C9EDA",
    deep: "#2E6FA3",
    soft: "#E5F1FA",
    dark: false,
    tagline: "Years ahead, mapped plainly",
    intro: "For people who are not sick and intend to stay that way. A calendar stretching years forward, written to inform, never to frighten.",
    core: "Its core is a timeline. What is worth doing this year, at forty, at fifty, at sixty.",
    signature: "timeline",
    features: [
      { icon: CalendarClock, path: "/timeline", name: "Your timeline", blurb: "A personal calendar across a life. Each item explained plainly, marked done, with optional reminders." },
      { icon: Search, path: "/screenings", name: "Every screening, both sides", blurb: "What it looks for, what it involves, who actually needs it, and the honest case for and against it." },
      { icon: Syringe, path: "/vaccines", name: "Vaccines across a life", blurb: "Including catch up schedules and travel." },
      { icon: ListTree, path: "/history", name: "Family history builder", blurb: "A simple tree that flags hereditary risk and tells you what to mention to a doctor." },
      { icon: Radar, path: "/signals", name: "Early signals", blurb: "What deserves attention early, written to inform rather than frighten." },
      { icon: Percent, path: "/risk", name: "The risk reader", blurb: "How to read risk numbers without panic. What a thirty percent increase actually means." },
      { icon: TrendingUp, path: "/moves", name: "The moves that matter", blurb: "The handful of habits that genuinely change outcomes, ranked by effect rather than by fashion." },
    ],
    room: "One room, mostly questions that start with should I.",
  },
];

export const CONCERNS = [
  { label: "I sleep badly", to: "/sleep", key: "sleep" },
  { label: "I want to stop", to: "/mental", key: "mental" },
  { label: "Something feels wrong in my body", to: "/health", key: "health" },
  { label: "I want to get strong", to: "/vitality", key: "vitality" },
  { label: "I feel alone", to: "/relationships", key: "relationships" },
  { label: "It is about cycles, fertility or midlife", to: "/reproductive", key: "reproductive" },
  { label: "I want to stay ahead of it", to: "/prevention", key: "prevention" },
];

export const byKey = (key) => PLATFORMS.find((p) => p.key === key);
export const byPath = (path) => PLATFORMS.find((p) => path.startsWith(p.path));

export const PLATFORM_TAGS = { general: { name: "Novcovek", hue: "#2E3338" } };
PLATFORMS.forEach((p) => { PLATFORM_TAGS[p.key] = { name: p.name, hue: p.hue }; });
